<script>
/**
 * @component AIPatientSummaryModal
 * @description Modal de resumen clínico del paciente generado por IA.
 *              Se abre desde la sala de espera antes de iniciar la atención.
 * @prop {boolean} visible - Controla visibilidad del modal
 * @prop {string} patientId - ID del paciente
 * @prop {string} patientName - Nombre del paciente para el título
 * @prop {string} doctorId - ID del médico que va a atender
 * @emits close - Al cerrar el modal
 */
import aiService from "@/services/ai";

export default {
  name: "AIPatientSummaryModal",

  props: {
    /** Controla visibilidad del modal */
    visible: { type: Boolean, default: false },
    /** ID del paciente */
    patientId: { type: String, default: "" },
    /** Nombre del paciente para el título */
    patientName: { type: String, default: "" },
    /** ID del médico que va a atender */
    doctorId: { type: String, default: "" },
  },

  data() {
    return {
      loading: false,
      summary: null,
      error: null,
    };
  },

  computed: {
    /**
     * @description Parsea el resumen en secciones estructuradas
     * @returns {Array}
     */
    parsedSummary() {
      if (!this.summary) return [];
      return this.summary
        .split("\n")
        .filter((l) => l.trim())
        .map((line) => {
          const match = line.match(/^\d+\.\s+\*\*([^*]+)\*\*[:\s]*(.*)$/);
          if (match) {
            return {
              isSection: true,
              title: match[1],
              body: match[2],
            };
          }
          return { isSection: false, text: line.replace(/\*\*/g, "") };
        });
    },
  },

  watch: {
    /**
     * @description Cuando el modal se abre, genera el resumen automáticamente
     */
    visible(val) {
      if (val && !this.summary) {
        this.generate();
      }
    },
  },

  methods: {
    /**
     * @description Llama al endpoint de resumen de paciente
     */
    async generate() {
      try {
        this.loading = true;
        this.error = null;
        const result = await aiService.patientSummary(
          this.patientId,
          this.doctorId
        );
        this.summary = result.summary;
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          "Error al generar el resumen del paciente.";
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description Cierra el modal y resetea el estado
     */
    close() {
      this.summary = null;
      this.error = null;
      this.$emit("close");
    },
  },
};
</script>

<template lang="pug">
el-dialog(
  :visible="visible"
  :title="''"
  width="680px"
  :before-close="close"
  custom-class="ai-summary-dialog"
)
  template(slot="title")
    .ai-summary-dialog__header
      .ai-summary-dialog__header-icon
        i.el-icon-magic-stick
      .ai-summary-dialog__header-text
        h3.ai-summary-dialog__title Resumen IA — {{ patientName }}
        p.ai-summary-dialog__subtitle Resumen clínico generado por inteligencia artificial

  .ai-summary-dialog__body
    //- Loading
    .ai-summary-dialog__loading(v-if="loading")
      i.el-icon-loading
      span Analizando historial del paciente...

    //- Error
    .ai-summary-dialog__error(v-else-if="error")
      i.el-icon-warning-outline
      span {{ error }}
      el-button(type="text" size="mini" @click="generate") Reintentar

    //- Contenido
    .ai-summary-dialog__content(v-else-if="summary")
      .ai-summary-section(
        v-for="(line, idx) in parsedSummary"
        :key="idx"
      )
        template(v-if="line.isSection")
          .ai-summary-section__header
            span.ai-summary-section__title {{ line.title }}
          p.ai-summary-section__body {{ line.body }}
        p.ai-summary-section__plain(v-else) {{ line.text }}

  template(slot="footer")
    .ai-summary-dialog__footer
      el-button(size="small" @click="generate" :loading="loading") Regenerar
      el-button(type="primary" size="small" @click="close") Cerrar
</template>

<style lang="scss" scoped>
.ai-summary-dialog {
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__header-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #e6f9f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 18px;
      color: #009b7d;
    }
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }

  &__subtitle {
    font-size: 12px;
    color: $text-secondary-new;
    margin: 0;
  }

  &__body {
    min-height: 200px;
    display: flex;
    flex-direction: column;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 20px;
    color: $text-secondary-new;
    font-size: 14px;

    i {
      font-size: 28px;
      color: $teal;
    }
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 20px;
    color: #dc2626;
    font-size: 13px;
    text-align: center;

    i {
      font-size: 24px;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

.ai-summary-dialog__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-summary-section {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.ai-summary-section__header {
  display: flex;
  align-items: center;
  padding: 7px 14px;
  background: #f0f4f8;
  border-bottom: 1px solid #e2e8f0;
}

.ai-summary-section__title {
  font-size: 11.5px;
  font-weight: 700;
  color: #002d5e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-family: "Plus Jakarta Sans", sans-serif;
}

.ai-summary-section__body {
  padding: 8px 12px;
  font-size: 13.5px;
  color: #334155;
  line-height: 1.5;
  margin: 0;
  font-family: "Plus Jakarta Sans", sans-serif;
}

.ai-summary-section__plain {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
  padding: 4px 0;
  font-family: "Plus Jakarta Sans", sans-serif;
}
</style>
